import * as React from 'react';
import './PlaceFilterDropdown.css';

// 「条件に一致する／一致しない」を、フィルタの種類に合わせた言葉で出すための文言
export interface PlaceFilterOperatorLabels {
  // メニュー先頭の選択欄で、項目名の後ろに続けて文として読める言葉（例: 「を含む」「を含まない」）
  match: string;
  notMatch: string;
  // ボタンの条件の後ろに付ける言葉。「一致する」ときは matchSuffix（空なら付けない）、「一致しない」ときは negatedSuffix
  matchSuffix: string;
  negatedSuffix: string;
}

interface PlaceFilterPopoverProps {
  label: string;
  // 設定中の条件の中身。指定するとボタンに「ラベル: 中身」と出し、長いときは省略する
  summary?: string;
  // ボタンに出す件数。0 なら出さず、1 以上なら絞り込み中の色にする
  activeCount: number;
  // 件数は出さずに、絞り込み中の色だけ付けたいとき（並び替えなど）
  active?: boolean;
  disabled?: boolean;
  menuClassName?: string;
  // 置いた直後からメニューを開いておくとき（フィルタを追加した直後など）
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // onNegatedChange と operatorLabels を指定すると、メニューの先頭に「項目名 ［を含む ▾］」の形の選択欄を出す
  negated?: boolean;
  onNegatedChange?: (negated: boolean) => void;
  operatorLabels?: PlaceFilterOperatorLabels;
  // 指定すると、ボタンの枠内の右端に × を出し、押すとフィルタごと外す
  onRemove?: () => void;
  // × の読み上げと説明に使う文言。指定しないときは「（ラベル）のフィルタを外す」にする
  removeLabel?: string;
  children: (close: () => void) => React.ReactNode;
}

// 物件一覧の絞り込みボタン。押すと下にメニューが開き、メニューの外を押すか Esc で閉じる
export const PlaceFilterPopover: React.FC<PlaceFilterPopoverProps> = function (props) {
  const [open, setOpenState] = React.useState<boolean>(props.defaultOpen ?? false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const onOpenChangeRef = React.useRef(props.onOpenChange);
  onOpenChangeRef.current = props.onOpenChange;
  const setOpen = React.useCallback((nextOpen: boolean) => {
    setOpenState(nextOpen);
    if (onOpenChangeRef.current) {
      onOpenChangeRef.current(nextOpen);
    }
  }, []);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, setOpen]);

  // ボタンの条件の後ろに「を含む」「以外」などの言葉を付ける。「一致しない」の言葉は太字にして目立たせる。
  // 条件が長くて省略されても隠れないよう、省略される部分の外に置く
  let operatorSuffix;
  if (props.summary && props.operatorLabels) {
    if (props.negated) {
      operatorSuffix = <span className="PlaceFilterDropdown-operator_suffix negated">{props.operatorLabels.negatedSuffix}</span>;
    } else if (props.operatorLabels.matchSuffix !== '') {
      operatorSuffix = <span className="PlaceFilterDropdown-operator_suffix">{props.operatorLabels.matchSuffix}</span>;
    }
  }
  let operatorSelect;
  if (props.onNegatedChange && props.operatorLabels) {
    const onNegatedChange = props.onNegatedChange;
    let operatorValue = 'match';
    if (props.negated) {
      operatorValue = 'notMatch';
    }
    operatorSelect = (
      <div className="PlaceFilterDropdown-operator">
        <span className="PlaceFilterDropdown-operator_label">{props.label}</span>
        <select
          className="PlaceFilterDropdown-operator_select"
          aria-label={`${props.label}の条件`}
          value={operatorValue}
          onChange={event => onNegatedChange(event.target.value === 'notMatch')}
        >
          <option value="match">{props.operatorLabels.match}</option>
          <option value="notMatch">{props.operatorLabels.notMatch}</option>
        </select>
      </div>
    );
  }
  let buttonClassName = 'PlaceFilterDropdown-button';
  if (props.active ?? props.activeCount > 0) {
    buttonClassName += ' active';
  }
  if (props.onRemove) {
    buttonClassName += ' removable';
  }
  let removeLabel = `${props.label}のフィルタを外す`;
  if (props.removeLabel) {
    removeLabel = props.removeLabel;
  }
  let menuClassName = 'PlaceFilterDropdown-menu';
  if (props.menuClassName) {
    menuClassName += ` ${props.menuClassName}`;
  }

  return (
    <div className="PlaceFilterDropdown-container" ref={containerRef}>
      <button type="button" className={buttonClassName} disabled={props.disabled} onClick={() => setOpen(!open)}>
        {props.summary ? (
          <span className="PlaceFilterDropdown-label" title={`${props.label}: ${props.summary}`}>
            {props.label}: {props.summary}
          </span>
        ) : (
          props.label
        )}
        {operatorSuffix}
        {props.activeCount > 0 && <span className="PlaceFilterDropdown-count">{props.activeCount}</span>}
        <span className="PlaceFilterDropdown-caret">▾</span>
      </button>
      {/* ボタンの中にボタンは置けないので、枠の上に重ねて枠内に見せる */}
      {props.onRemove && (
        <button type="button" className="PlaceFilterDropdown-remove" aria-label={removeLabel} title={removeLabel} onClick={() => props.onRemove!()}>
          ×
        </button>
      )}
      {open && (
        <div
          className={menuClassName}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        >
          {operatorSelect}
          {props.children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
};
